import { css } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { checkAggregateACL, isWeaveUser } from '@frontend/auth-helpers';
import { DashboardWidget, DashboardWidgetFC, TimePeriodListBoxMenu } from '@frontend/grid-dashboard';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { CI_UNHAPPY_WIDGETS_FF } from '../../constants';
import { CallList } from './CallList';
import { SummaryPanel } from './SummaryPanel';

/**
 * @dashboard-widget
 *
 * id: call-intel-unhappy-calls-widget
 * title: Call Intelligence - Unhappy Calls
 * description: Keep a pulse on your patient experience and improve satisfaction with a weekly list of unhappy calls.
 * icon: emoji-sad-small
 */
export const CallIntelUnhappyCallsWidget: DashboardWidgetFC = () => {
  const { t } = useTranslation('callIntelDashboardWidgets');
  const { selectedLocationIds, isSingleTypeScope } = useAppScopeStore();
  const { getFeatureFlagLocationMap } = useScopedAppFlagStore();

  const widgetFFLocationMap = getFeatureFlagLocationMap(CI_UNHAPPY_WIDGETS_FF);
  const widgetEnabledLocationIds = selectedLocationIds.filter((locationId) => widgetFFLocationMap[locationId]?.value);

  const { aclValuesByLocationId } = checkAggregateACL(selectedLocationIds, [Permission.CALL_CLASSIFICATION_READ]);

  const locationIdsWithPermission = isWeaveUser()
    ? widgetEnabledLocationIds
    : widgetEnabledLocationIds.filter((locationId) => aclValuesByLocationId[locationId]);

  return (
    <DashboardWidget>
      <DashboardWidget.Header icon='call-intel-small' title={t('Call Intelligence: Unhappy Calls')}>
        <div css={{ flexShrink: 0 }}>
          <TimePeriodListBoxMenu value='week' readonly />
        </div>
      </DashboardWidget.Header>
      <DashboardWidget.Content css={styles.container}>
        <div css={styles.contentLayout}>
          <SummaryPanel locationIds={locationIdsWithPermission} />
          <CallList locationIds={locationIdsWithPermission} showLocationChip={!isSingleTypeScope} />
        </div>
      </DashboardWidget.Content>
    </DashboardWidget>
  );
};

CallIntelUnhappyCallsWidget.config = {
  size: {
    large: 'large-wide',
    medium: 'large-wide',
    small: 'large-narrow',
    extraSmall: 'large-narrow',
  },
  feature: 'analytics',
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    margin-top: ${theme.spacing(2)};
    container-type: inline-size;
  `,
  contentLayout: css`
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: ${theme.spacing(2)};
    @container (max-width: ${breakpoints.small.min}px) {
      flex-direction: column;
    }
  `,
  filter: css`
    gap: ${theme.spacing(0.5)};
    flex-shrink: 0;
  `,
};
